






















































































































































































import { Vue, Component, Prop, PropSync, Watch } from "vue-property-decorator";
import store from "@/store";
//  types
import { Header, Row } from "@/types/VDataTable";
//  components
import DataCell from "./DataCell.vue";
import SortableArrows from "@/components/SortableArrows.vue";
import Tooltip from "@/components/Tooltip.vue";
//  modules
import { getModule } from "vuex-module-decorators";
import IncidentsModule from "@/store/clients/Incidents.module";
import VesselsModule from "@/store/clients/Vessels.module";
import VesselGroupsModule from "@/store/clients/VesselGroups.module";
import LongTrendsModule from "@/store/clients/LongTrends.module";
import MissingPositionData from "@/assets/icons/MissingPositionData.vue";

const Incidents = getModule(IncidentsModule, store);
const Vessels = getModule(VesselsModule, store);
const VesselGroups = getModule(VesselGroupsModule, store);
const LongTrends = getModule(LongTrendsModule, store);

@Component({
  components: {
    DataCell,
    SortableArrows,
    Tooltip,
  },
})
export default class FleetOverviewTable extends Vue {
  @Prop({ type: Boolean, default: false }) loading!: Boolean;
  @Prop({ type: Array, default: () => [] }) headers!: Header[];
  @Prop({ type: Array, default: () => [] }) rows!: Row[];
  @PropSync("selectedRows", { type: Array, default: () => [] }) syncSelectedRows!: object[];
  @PropSync("searchQuery") syncSearchQuery!: string;

  //  @Data
  sortBy = "";
  isLoadingTextDisplay = false;
  sortDesc = false;
  pageLimit = 10;
  pageCount = 0;
  currentItems: Row[] = [];
  currentPage = 1;
  syncRows: Row[] = [];

  // @Watchers
  updateSortBy(value: string): void {
    if (!value) value = "";
    localStorage.setItem("vesselOverview_sortBy", value);
  }

  updateSortDesc(value: boolean): void {
    if (!value) value = false;
    localStorage.setItem("vesselOverview_sortDesc", value.toString());
  }

  updateItemsPerPage(value: number): void {
    if (!value) value = 10;
    localStorage.setItem("vesselOverview_itemsPerPage", value.toString());
    this.pageLimit = value;
  }

  //  @Getters
  get isSomeRowSelected(): boolean {
    return Boolean(this.syncSelectedRows.length > 0);
  }

  get isLessThanLimit(): boolean {
    return this.rows.length <= this.pageLimit;
  }

  get skeletonStructure(): object {
    if (this.isVesselGroupColumnAvailable) return { "table-row": "table-cell@5" };
    return { "table-row": "table-cell@4" };
  }

  get filteredHeaders(): Header[] {
    const result = this.headers.filter(header => {
      if (header.value === "vesselGroup" && !this.isVesselGroupColumnAvailable) {
        return false; // Exclude "Vessel Groups" column if no vessel groups exist
      }
      return true;
    });
    return result;
  }

  get isVesselGroupColumnAvailable(): boolean {
    return VesselGroups.vesselGroups.length > 0;
  }

  get sortedRows() {
    if (!this.sortBy) return this.rows;

    return this.rows.slice().sort((a, b) => {
      // Special handling for Last Data Observed column
      if (this.sortBy === "lastDataObserved") {
        const dateA = new Date(a[this.sortBy]?.value ?? "");
        const dateB = new Date(b[this.sortBy]?.value ?? "");
        if (this.sortDesc) {
          return dateB.getTime() - dateA.getTime();
        } else {
          return dateA.getTime() - dateB.getTime();
        }
      }

      // Default string/other sorting
      const valueA = a[this.sortBy]?.value ?? "";
      const valueB = b[this.sortBy]?.value ?? "";

      // Normalize values for comparison
      const normalizedA = typeof valueA === "string" ? valueA.toLowerCase() : valueA;
      const normalizedB = typeof valueB === "string" ? valueB.toLowerCase() : valueB;

      if (this.sortDesc) {
        return normalizedA < normalizedB ? 1 : normalizedA > normalizedB ? -1 : 0;
      } else {
        return normalizedA > normalizedB ? 1 : normalizedA < normalizedB ? -1 : 0;
      }
    });
  }

  //  @Methods
  cellData(row: Row, header: Header, index: number): any {
    return {
      column: header.value,
      columnName: header.text,
      vesselId: row.vesselName.vesselId,
      vesselName: row.vesselName.value,
      type: header.type,
      align: header.align,
      value: row[header.value].value,
      subtitle: row[header.value].subtitle,
      warningList: row[header.value].warningList,
      criticalList: row[header.value].criticalList,
      link: row[header.value].link,
      missingPosition: row[header.value].missingPosition,
    };
  }

  headerClasses(header: any): string[] {
    return [
      header.class,
      {
        sortable: header.sortable,
        "no-sort": this.sortBy !== header.value,
      },
    ];
  }

  headerStyles(header: any): any {
    return {
      width: header.width ? header.width : "auto",
      "min-width": header.width ? header.width : "auto",
    };
  }

  isDiagnosticsEnabled(vesselId: number): any {
    const vessel = Vessels.getExtendedVesselById(vesselId);
    return vessel ? vessel.features.some(feature => feature.name === "Diagnostics") : false;
  }

  async initializeTable(): Promise<void> {
    this.sortBy = localStorage.getItem("vesselOverview_sortBy") || "";
    this.sortDesc = localStorage.getItem("vesselOverview_sortDesc") === "true";
    this.pageLimit = Number(localStorage.getItem("vesselOverview_itemsPerPage")) || 10;

    //  condition thresholds required for Alert tooltip data in DataIndicator
    await Incidents.fetchConditions();
  }

  sort(value: string): void {
    if (this.sortBy === value) {
      this.sortDesc = !this.sortDesc; // Toggle sort direction
      if (!this.sortDesc) this.sortBy = ""; // Reset sorting if toggled off
    } else {
      this.sortBy = value;
      this.sortDesc = false; // Default to ascending order
    }
  }

  closeToast(): void {
    this.syncSelectedRows = [];
  }

  clearSearch(): void {
    this.syncSearchQuery = "";
  }

  //  @Hooks
  async created(): Promise<void> {
    await this.initializeTable();
  }

  goToComparison() {
    const rows = this.syncSelectedRows as { vesselName: { vesselId: number } }[];
    const vesselIds = rows.map(row => row.vesselName.vesselId);
    this.$router.push({ name: "VesselComparison", params: { vesselIds: vesselIds.join() } });
  }
}
